import React, { useState, useEffect, useCallback } from "react";
import client from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Input } from "../../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import FinancialAnalysis from "./FinancialAnalysis";
import NotesPanel from "../../components/NotesPanel";
import { PropertyMedia } from "../../components/PropertyMedia";
import { RankSelect, DisqualifyButton, DisqualifiedBanner, RankBadge, DisqualifiedBadge } from "../../components/DealControls";
import { LegalDocuments } from "../../components/LegalDocuments";
import { LocationCard, MapPicker } from "../../components/Maps";
import { AgentPicker } from "../../components/AgentPicker";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../i18n/LangContext";
import CurrencySwitcher from "../../components/CurrencySwitcher";
import { useCurrency } from "../../lib/currency";
import { LOOM_HELP_URL, telHref, waHref, mailHref } from "../../mock";
import {
  ChevronLeft, Star, Sparkles, RefreshCw, Loader2, ArrowUpRight, Plus,
  Lock, KeyRound, PlayCircle, Check, Pencil, X, Building2, MapPin, ChevronDown, Search, Trash2, Languages,
  Phone, MessageCircle, Mail,
} from "lucide-react";
import { toast } from "sonner";

const ADD_NEW = "__add_new__";

// Lightweight fuzzy matcher: tolerates typos / missing characters.
function fuzzyScore(query, target) {
  query = (query || "").toLowerCase().trim();
  target = (target || "").toLowerCase();
  if (!query) return 1;
  if (target.includes(query)) return 100 - (target.length - query.length) * 0.1;
  // subsequence match (handles missing characters)
  let qi = 0, gaps = 0, last = -1;
  for (let i = 0; i < target.length && qi < query.length; i++) {
    if (target[i] === query[qi]) { if (last >= 0) gaps += i - last - 1; last = i; qi++; }
  }
  if (qi === query.length) return 60 - gaps * 0.5;
  // allow up to ~2 char mismatches (transpositions/typos) via matched-char ratio
  let matched = 0; const t = target.split("");
  for (const ch of query) { const idx = t.indexOf(ch); if (idx >= 0) { matched++; t.splice(idx, 1); } }
  const ratio = matched / query.length;
  return ratio >= 0.7 ? 30 * ratio : -1;
}

function rankAgents(agents, query) {
  if (!query.trim()) return agents.slice(0, 3); // last 3 (list already newest-first)
  return agents
    .map((a) => ({ a, s: Math.max(fuzzyScore(query, a.name), fuzzyScore(query, a.agency)) }))
    .filter((x) => x.s > 0)
    .sort((x, y) => y.s - x.s)
    .slice(0, 6)
    .map((x) => x.a);
}


function Markdown({ text }) {
  if (!text) return null;
  const bold = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((seg, i) =>
    seg.startsWith("**") && seg.endsWith("**")
      ? <strong key={i} className="text-white font-semibold">{seg.slice(2, -2)}</strong>
      : <span key={i}>{seg}</span>);
  return (
    <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
      {text.split("\n").filter((l) => l.trim() && l.trim() !== "--" && l.trim() !== "---").map((line, i) => {
        const t = line.trim();
        if (t.startsWith("### ")) return <h4 key={i} className="font-display text-base font-semibold text-white pt-2">{bold(t.slice(4))}</h4>;
        if (t.startsWith("## ")) return <h3 key={i} className="font-display text-lg font-semibold text-white pt-2">{bold(t.slice(3))}</h3>;
        return <p key={i}>{bold(t)}</p>;
      })}
    </div>
  );
}

export default function PropertyDetail({ id, agents = [], onBack, onChanged }) {
  const currency = useCurrency();
  const [p, setP] = useState(null);
  const [cities, setCities] = useState(["Marrakech"]);
  const [areas, setAreas] = useState([]);
  const [analysing, setAnalysing] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [compUrl, setCompUrl] = useState("");
  const [usage, setUsage] = useState(null);
  const [howOpen, setHowOpen] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState({});
  const [addLoc, setAddLoc] = useState(null);          // { kind, name }
  const [defaults, setDefaults] = useState({ cities: [], areas: [] });
  const [descEdit, setDescEdit] = useState(false);
  const [descDraft, setDescDraft] = useState("");
  const [savingDesc, setSavingDesc] = useState(false);
  const [agentOpen, setAgentOpen] = useState(false);
  const [agentQuery, setAgentQuery] = useState("");
  const [delOpen, setDelOpen] = useState(false);
  const [delText, setDelText] = useState("");
  const [delProgress, setDelProgress] = useState(null); // null = idle, 0-100 while counting down
  const delTimer = React.useRef(null);
  const [transLang, setTransLang] = useState("original");
  const [transText, setTransText] = useState("");
  const [translating, setTranslating] = useState(false);
  const [offerForm, setOfferForm] = useState(null); // null or { type, valueMad, note }
  const [titleEdit, setTitleEdit] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const [locDraft, setLocDraft] = useState({});
  const { user: me } = useAuth();
  const { t } = useLang();
  const changeAgent = async (agentId) => {
    try { const r = await client.post(`/properties/${p.id}/agent`, { agentId }); setP(r.data); onChanged && onChanged(r.data); toast.success(t("Agent changed (logged)")); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change agent"); }
  };
  const [titleDraft, setTitleDraft] = useState("");
  const saveTitle = async () => {
    const t = titleDraft.trim();
    setTitleEdit(false);
    if (!t || t === p.title) return;
    await patch({ title: t });
  };

  const loadUsage = useCallback(async () => {
    try { const r = await client.get("/ai/usage"); setUsage(r.data); } catch (e) { console.error("AI usage load failed", e); }
  }, []);
  const loadLocations = useCallback(async () => {
    try {
      const [r, d] = await Promise.all([client.get("/meta/locations"), client.get("/meta/defaults")]);
      setCities(r.data.cities); setAreas(r.data.areas); setDefaults(d.data);
    } catch (e) { console.error("Locations load failed", e); }
  }, []);
  const deleteLoc = async (kind, name) => {
    try {
      const r = await client.post("/meta/locations/delete", { kind, name });
      setCities(r.data.cities); setAreas(r.data.areas);
      toast.success(`${kind === "city" ? "City" : "Area"} deleted`);
    } catch (e) { toast.error("Can't delete", { description: e?.response?.data?.detail }); }
  };
  const load = useCallback(async () => {
    try {
      const res = await client.get(`/properties/${id}`);
      setP(res.data);
    } catch (e) { toast.error("Couldn't load property"); }
  }, [id]);
  useEffect(() => { load(); loadUsage(); loadLocations(); }, [load, loadUsage, loadLocations]);

  const patch = async (body, silent) => {
    try {
      const res = await client.put(`/properties/${id}`, body);
      setP(res.data);
      onChanged && onChanged(res.data);
      if (!silent) toast.success("Saved");
      return res.data;
    } catch (e) { toast.error("Couldn't save", { description: e?.response?.data?.detail }); }
  };

  // ---- delete with typed confirm + 7s undo countdown ----
  const DEL_MS = 7000;
  const startDelete = () => {
    if (delText.trim().toUpperCase() !== "DELETE") return;
    setDelProgress(0);
    const start = Date.now();
    delTimer.current = setInterval(() => {
      const pct = Math.min(100, ((Date.now() - start) / DEL_MS) * 100);
      setDelProgress(pct);
      if (pct >= 100) { clearInterval(delTimer.current); delTimer.current = null; doDelete(); }
    }, 40);
  };
  const undoDelete = () => {
    if (delTimer.current) clearInterval(delTimer.current);
    delTimer.current = null;
    setDelProgress(null);
    toast.success("Delete cancelled");
  };
  const closeDel = (o) => {
    if (o) return;
    if (delProgress !== null) return; // don't allow closing mid-countdown via overlay
    setDelOpen(false); setDelText("");
  };
  const doDelete = async () => {
    try {
      await client.delete(`/properties/${id}`);
      toast.success("Property deleted");
      setDelOpen(false); setDelText(""); setDelProgress(null);
      onBack && onBack();
    } catch (e) {
      setDelProgress(null);
      toast.error("Couldn't delete", { description: e?.response?.data?.detail });
    }
  };
  useEffect(() => () => { if (delTimer.current) clearInterval(delTimer.current); }, []);

  // ---- location select with "add new" ----
  const onCity = (v) => { if (v === ADD_NEW) setAddLoc({ kind: "city", name: "" }); else patch({ city: v }); };
  const onArea = (v) => { if (v === ADD_NEW) setAddLoc({ kind: "area", name: "" }); else patch({ district: v }); };
  const saveLoc = async () => {
    const name = (addLoc.name || "").trim();
    if (!name) return;
    try {
      const r = await client.post("/meta/locations", { kind: addLoc.kind, name });
      setCities(r.data.cities); setAreas(r.data.areas);
      await patch(addLoc.kind === "city" ? { city: name } : { district: name }, true);
      toast.success(`${addLoc.kind === "city" ? "City" : "Area"} added`);
      setAddLoc(null);
    } catch (e) { toast.error("Couldn't add"); }
  };

  // ---- edit specs ----
  const openEdit = () => {
    setEdit({ title: p.title, beds: p.beds, baths: p.baths, size: p.size, priceMad: p.priceMad || p.rentMad || 0, listingType: p.listingType || "rental" });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    const lt = edit.listingType;
    if (!(edit.title || "").trim()) { toast.error("Title can't be empty"); return; }
    await patch({
      title: edit.title.trim(),
      beds: Number(edit.beds) || 0, baths: Number(edit.baths) || 0, size: Number(edit.size) || 0,
      priceMad: Number(edit.priceMad) || 0, listingType: lt, period: lt === "rental" ? "month" : null,
    });
    setEditOpen(false);
  };
  // auto-suggest type from price (>=200k MAD = purchase) but editable
  const onEditPrice = (v) => {
    const price = Number(v) || 0;
    setEdit((e) => ({ ...e, priceMad: v, listingType: e._touchedType ? e.listingType : (price >= 200000 ? "purchase" : "rental") }));
  };

  const analyse = async () => {
    setAnalysing(true);
    try { const res = await client.post(`/properties/${id}/analyse`); setP((x) => ({ ...x, analysis: res.data.analysis })); toast.success("AI analysis ready"); loadUsage(); }
    catch (e) { if (e?.response?.status === 402) { loadUsage(); toast.error("Free AI credit used up", { description: e.response.data.detail }); } else toast.error("Analysis failed", { description: e?.response?.data?.detail }); }
    finally { setAnalysing(false); }
  };
  const estimate = async () => {
    setEstimating(true);
    try { const res = await client.post(`/properties/${id}/airbnb`); setP((x) => ({ ...x, airbnb: res.data })); toast.success("Airbnb estimate ready"); loadUsage(); }
    catch (e) { if (e?.response?.status === 402) { loadUsage(); toast.error("Free AI credit used up", { description: e.response.data.detail }); } else toast.error("Estimate failed", { description: e?.response?.data?.detail }); }
    finally { setEstimating(false); }
  };

  const addComp = async () => {
    if (!compUrl.trim()) return;
    await patch({ comparables: [...(p.comparables || []), { url: compUrl.trim(), added: new Date().toISOString() }] }, true);
    setCompUrl(""); toast.success("Comparable added");
  };

  const saveDesc = async () => {
    setSavingDesc(true);
    await patch({ summary: descDraft }, true);
    setSavingDesc(false); setDescEdit(false);
    setTransLang("original"); setTransText("");
    toast.success("Description saved");
  };

  // ---- AI translation of description ----
  const LANGS = [["original", "Original"], ["en", "English"], ["fr", "Français"], ["ar", "العربية"], ["nl", "Nederlands"], ["es", "Español"]];
  const translateTo = async (lang) => {
    if (lang === "original") { setTransLang("original"); setTransText(""); return; }
    if (!p.summary) { toast.error("Add a description first"); return; }
    setTranslating(true); setTransLang(lang);
    try {
      const r = await client.post("/ai/translate", { text: p.summary, lang });
      setTransText(r.data.text);
    } catch (e) {
      setTransLang("original");
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402) setHowOpen(true);
      else toast.error("Couldn't translate", { description: d });
    } finally { setTranslating(false); }
  };

  // ---- negotiation / offers ----
  const OFFER_TYPES = ["Offer made", "Counter received", "Counter sent", "Agreed", "Rejected", "Viewing booked", "Contract signed"];
  const offers = p?.offers || [];
  const saveOffer = async () => {
    const f = offerForm;
    const entry = { type: f.type || OFFER_TYPES[0], valueMad: Number(f.valueMad) || 0, note: (f.note || "").trim(), ts: new Date().toISOString() };
    const next = [entry, ...offers];
    const res = await patch({ offers: next }, true);
    if (res) { setOfferForm(null); toast.success("Event saved"); }
  };
  const removeOffer = async (ts) => {
    const next = offers.filter((o) => o.ts !== ts);
    await patch({ offers: next }, true);
    toast.success("Event removed");
  };

  const saveKey = async () => {
    if (!keyInput.trim()) { toast.error("Paste your Claude API key"); return; }
    setSavingKey(true);
    try { const res = await client.post("/ai/key", { ai_key: keyInput.trim() }); setUsage(res.data); setKeyInput(""); setHowOpen(false); toast.success("Claude key saved", { description: "AI unlocked — runs use your own key." }); }
    catch (e) { toast.error("Couldn't save key"); } finally { setSavingKey(false); }
  };

  if (!p) return <div className="grid h-64 place-items-center"><div className="h-8 w-8 rounded-full border-2 border-brand-red border-t-transparent animate-spin" /></div>;

  const isRental = p.listingType === "rental";
  const ab = p.airbnb || {};
  const assignedIds = (p.agentIds && p.agentIds.length) ? p.agentIds : (p.agentId ? [p.agentId] : []);
  const linkedAgents = assignedIds.map((aid) => agents.find((a) => a.id === aid)).filter(Boolean);
  const addAgentToProp = (aid) => { if (!assignedIds.includes(aid)) patch({ agentIds: [...assignedIds, aid] }, true); };
  const removeAgentFromProp = (aid) => patch({ agentIds: assignedIds.filter((x) => x !== aid) }, true);
  const blocked = usage?.blocked;
  const ownKey = usage?.has_own_key;
  const remaining = usage?.remaining ?? 0;

  const Stat = ({ value, label }) => (
    <div className="rounded-xl border border-white/8 bg-white/[0.02] p-4 text-center">
      <p className="font-display text-2xl font-semibold text-white">{value}</p>
      <p className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
    </div>
  );
  const CreditBadge = () => {
    if (!usage) return null;
    if (ownKey) return <span className="rounded-full border border-brand-green/30 bg-brand-green/10 px-2 py-0.5 text-[11px] text-brand-green">Your key · active</span>;
    return <span className={`rounded-full border px-2 py-0.5 text-[11px] ${remaining > 0 ? "border-white/15 bg-white/5 text-muted-foreground" : "border-brand-red/30 bg-brand-red/10 text-brand-red"}`}>Free AI credit: ${remaining.toFixed(2)} left</span>;
  };
  const BlockedNotice = () => (
    <div className="rounded-xl border border-brand-red/25 bg-brand-red/5 p-4">
      <div className="flex items-start gap-3">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-red/15"><Lock className="h-4 w-4 text-brand-red" /></div>
        <div className="flex-1">
          <p className="text-sm font-medium text-white">You've used your $3 of free AI credit</p>
          <p className="mt-0.5 text-xs text-muted-foreground">Add your own Claude API key to keep running AI analysis — billed to your own Anthropic account.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setHowOpen(true)} className="bg-brand-red hover:bg-brand-redDark text-white"><KeyRound className="h-4 w-4" /> Add your Claude key</Button>
            <Button size="sm" variant="outline" onClick={() => setHowOpen(true)} className="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]"><PlayCircle className="h-4 w-4" /> Watch how</Button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-white transition-colors">
          <ChevronLeft className="h-4 w-4" /> {t("Back to properties")}
        </button>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <CurrencySwitcher currency={currency} />
          <RankSelect property={p} onChange={(u) => { setP(u); onChanged && onChanged(u); }} disabled={p.disposition === "disqualified"} />
          <DisqualifyButton property={p} onPatch={(b) => patch(b, true)} />
          <Button data-testid="edit-property-btn" onClick={openEdit} variant="outline" className="h-10 border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]"><Pencil className="h-4 w-4" /> {t("Edit")}</Button>
          <Button data-testid="delete-property-btn" onClick={() => { setDelText(""); setDelProgress(null); setDelOpen(true); }}
            variant="outline" className="h-10 border-brand-red/40 bg-brand-red/10 text-brand-red hover:bg-brand-red/20"><Trash2 className="h-4 w-4" /> {t("Delete")}</Button>
        </div>
      </div>

      <DisqualifiedBanner property={p} />

      {/* Gallery + files */}
      <PropertyMedia property={p} onChange={setP} onPatch={(b) => patch(b, true)} />

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <div className="flex items-center justify-between"><p className="text-[11px] uppercase tracking-widest text-muted-foreground">{t("Location")}</p>
            {p.listingStatus && p.listingStatus !== "Available" && <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-white" data-testid="detail-listing-status">{t(p.listingStatus)}</span>}</div>
          <LocationCard property={p} onAdd={() => { setLocDraft({ latitude: p.latitude, longitude: p.longitude, mapsLink: p.mapsLink, locationApproximate: p.locationApproximate }); setLocOpen(true); }} />
          {me?.role === "super_admin" && (
            <div data-testid="change-agent-box"><p className="mb-1 text-[11px] uppercase tracking-widest text-muted-foreground">{t("Agent (super admin can change — every change is logged)")}</p>
              <AgentPicker value={p.agentId} onChange={(id) => { if (id !== p.agentId) changeAgent(id); }} /></div>
          )}
        </div>
        <LegalDocuments propertyId={p.id} />
      </div>
      <Dialog open={locOpen} onOpenChange={setLocOpen}>
        <DialogContent className="glass-strong max-w-lg border-white/10" data-testid="location-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl text-white">{t("Property location")}</DialogTitle></DialogHeader>
          <MapPicker value={locDraft} onChange={(v) => setLocDraft((d) => ({ ...d, ...(typeof v === "function" ? v(d) : v) }))} />
          <Button onClick={async () => { await patch({ latitude: locDraft.latitude ?? null, longitude: locDraft.longitude ?? null, mapsLink: locDraft.mapsLink || "", locationApproximate: !!locDraft.locationApproximate }, true); setLocOpen(false); toast.success(t("Location saved")); }} data-testid="location-save" className="h-10 w-full bg-brand-red text-white hover:bg-brand-redDark">{t("Save location")}</Button>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            {titleEdit ? (
              <form onSubmit={(e) => { e.preventDefault(); saveTitle(); }} className="flex items-center gap-2">
                <Input data-testid="title-inline-input" autoFocus value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} onBlur={saveTitle}
                  onKeyDown={(e) => { if (e.key === "Escape") setTitleEdit(false); }} className="h-11 w-[min(70vw,520px)] bg-input/60 border-white/10 font-display text-2xl font-bold text-white focus-visible:ring-brand-red" />
              </form>
            ) : (
              <button data-testid="title-edit-btn" onClick={() => { setTitleDraft(p.title); setTitleEdit(true); }} title="Click to rename" className="group flex items-center gap-2 text-left">
                <h1 className="font-display text-3xl font-bold text-white">{p.title}</h1>
                <Pencil className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </button>
            )}
            <RankBadge rank={p.rank} />
            {p.disposition === "disqualified" && <DisqualifiedBadge reason={p.disqualifyReason} />}
          </div>
          <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{[p.district, p.city].filter(Boolean).join(", ") || "Marrakech"}</p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Select value={p.city || "Marrakech"} onValueChange={onCity}>
              <SelectTrigger className="h-11 w-36 bg-white/[0.03] border-white/10 focus:ring-brand-red"><Building2 className="mr-1 h-3.5 w-3.5 text-muted-foreground" /><SelectValue placeholder="City" /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10 max-h-64">
                {cities.map((c) => <SelectItem key={c} value={c} className="focus:bg-white/5">{c}</SelectItem>)}
                <SelectItem value={ADD_NEW} className="text-brand-red focus:bg-brand-red/10">+ Add city…</SelectItem>
              </SelectContent>
            </Select>
            <Select value={p.district || ""} onValueChange={onArea}>
              <SelectTrigger className="h-11 w-40 bg-white/[0.03] border-white/10 focus:ring-brand-red"><SelectValue placeholder={t("Area")} /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10 max-h-64">
                {areas.map((a) => <SelectItem key={a} value={a} className="focus:bg-white/5">{a}</SelectItem>)}
                <SelectItem value={ADD_NEW} className="text-brand-red focus:bg-brand-red/10">+ Add area…</SelectItem>
              </SelectContent>
            </Select>
            <Popover open={agentOpen} onOpenChange={(o) => { setAgentOpen(o); if (!o) setAgentQuery(""); }}>
              <PopoverTrigger asChild>
                <button className="flex h-11 items-center justify-between gap-2 rounded-md border border-white/10 bg-white/[0.03] px-3 text-sm text-white hover:bg-white/[0.06] transition-colors" data-testid="add-agent-trigger">
                  <span className="truncate">{linkedAgents.length ? `${linkedAgents.length} agent${linkedAgents.length > 1 ? "s" : ""}` : t("Agent")}</span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 glass-strong border-white/10 p-0">
                <div className="max-h-72 overflow-y-auto p-1.5">
                  <p className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">{agentQuery ? "Matches" : "Add an agent"}</p>
                  {rankAgents(agents.filter((a) => !assignedIds.includes(a.id)), agentQuery).map((a) => (
                    <button key={a.id} onClick={() => { addAgentToProp(a.id); setAgentOpen(false); setAgentQuery(""); }}
                      data-testid="assign-agent-option"
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm transition-colors hover:bg-white/5">
                      <img src={a.avatar} alt="" className="h-7 w-7 rounded-lg object-cover" />
                      <span className="min-w-0 flex-1"><span className="block truncate text-white">{a.name}</span><span className="block truncate text-[11px] text-muted-foreground">{a.agency}{a.city ? ` · ${a.city}` : ""}</span></span>
                    </button>
                  ))}
                  {agents.length === 0 && <div className="px-2 py-2 text-xs text-muted-foreground">No agents yet — add one in the Agents tab.</div>}
                  {agents.length > 0 && agents.filter((a) => !assignedIds.includes(a.id)).length === 0 && <div className="px-2 py-2 text-xs text-muted-foreground">All agents are already assigned.</div>}
                  {agentQuery && rankAgents(agents.filter((a) => !assignedIds.includes(a.id)), agentQuery).length === 0 && <div className="px-2 py-2 text-xs text-muted-foreground">No close matches.</div>}
                </div>
                <div className="border-t border-white/10 p-1.5">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <input autoFocus value={agentQuery} onChange={(e) => setAgentQuery(e.target.value)} placeholder="Search agent name…"
                      className="h-9 w-full rounded-lg border border-white/10 bg-input/60 pl-8 pr-2 text-sm text-white outline-none focus:ring-1 focus:ring-brand-red placeholder:text-muted-foreground" />
                  </div>
                  <p className="px-1 pt-1.5 text-[10px] text-muted-foreground">You can only assign agents already on the platform.</p>
                </div>
              </PopoverContent>
            </Popover>
            <Button variant="outline" onClick={() => patch({ favourite: !p.favourite }, true)}
              className={`h-11 border-white/12 bg-white/[0.03] hover:bg-white/[0.07] ${p.favourite ? "text-amber-400" : "text-white"}`}>
              <Star className={`h-4 w-4 ${p.favourite ? "fill-amber-400" : ""}`} /> {t("Favourite")}
            </Button>
          </div>
          {linkedAgents.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="property-agent-contacts">
              {linkedAgents.map((la) => (
                <div key={la.id} className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] py-1.5 pl-2 pr-1.5" data-testid="assigned-agent-chip">
                  <img src={la.avatar} alt="" className="h-7 w-7 rounded-lg object-cover" />
                  <div className="min-w-0">
                    <span className="block truncate text-sm text-white">{la.name}</span>
                    {la.city && <span className="block truncate text-[10px] text-brand-green">{la.city}</span>}
                  </div>
                  <div className="ml-1 flex items-center gap-1">
                    {telHref(la.phone) && (
                      <a href={telHref(la.phone)} title="Call agent" data-testid="prop-agent-call"
                        className="grid h-8 w-8 place-items-center rounded-md border border-white/12 bg-white/[0.03] text-white transition-colors hover:bg-white/[0.08]"><Phone className="h-3.5 w-3.5" /></a>
                    )}
                    {waHref(la.whatsapp || la.phone) && (
                      <a href={waHref(la.whatsapp || la.phone)} target="_blank" rel="noreferrer" title="WhatsApp agent" data-testid="prop-agent-whatsapp"
                        className="grid h-8 w-8 place-items-center rounded-md border border-brand-green/30 bg-brand-green/10 text-brand-green transition-colors hover:bg-brand-green/20"><MessageCircle className="h-3.5 w-3.5" /></a>
                    )}
                    {mailHref(la.email) && (
                      <a href={mailHref(la.email)} title="Email agent" data-testid="prop-agent-email"
                        className="grid h-8 w-8 place-items-center rounded-md border border-white/12 bg-white/[0.03] text-white transition-colors hover:bg-white/[0.08]"><Mail className="h-3.5 w-3.5" /></a>
                    )}
                    <button onClick={() => removeAgentFromProp(la.id)} title="Remove agent" data-testid="remove-agent-btn"
                      className="grid h-8 w-8 place-items-center rounded-md border border-brand-red/30 bg-brand-red/10 text-brand-red transition-colors hover:bg-brand-red/20"><X className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="text-left lg:text-right">
          <p className="font-display text-4xl font-bold text-white">{(p.priceMad || p.rentMad || 0).toLocaleString()} <span className="text-2xl">MAD</span></p>
          <p className="text-sm text-muted-foreground">{currency.fmt(p.priceMad || p.rentMad || 0)}{isRental ? " /month" : " · purchase"}</p>
          <span className={`mt-1 inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${isRental ? "border-brand-green/30 bg-brand-green/10 text-brand-green" : "border-brand-red/30 bg-brand-red/10 text-brand-red"}`}>{isRental ? "Rent" : "Purchase"}</span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat value={p.beds} label="Beds" />
        <Stat value={p.baths} label="Baths" />
        <Stat value={p.size || "—"} label="m²" />
        <Stat value={p.sourceName || "manual"} label="Source" />
      </div>
      {p.sourceUrl && (
        <a href={p.sourceUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-brand-red hover:text-brand-green transition-colors">
          View original listing <ArrowUpRight className="h-3.5 w-3.5" />
        </a>
      )}

      {/* Description */}
      <div className="rounded-2xl border border-white/[0.06] bg-card/40 p-5 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-display text-lg font-semibold text-white">{t("Description")}</h3>
          {!descEdit && (
            <Button variant="outline" size="sm" onClick={() => { setDescDraft(p.summary || ""); setDescEdit(true); }}
              className="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]"><Pencil className="h-3.5 w-3.5" /> {t("Edit")}</Button>
          )}
        </div>
        {!descEdit && p.summary && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="translate-bar">
            <span className="mr-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Languages className="h-3.5 w-3.5" /> Translate:</span>
            {LANGS.map(([code, label]) => (
              <button key={code} data-testid={`translate-${code}`} disabled={translating} onClick={() => translateTo(code)}
                className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors disabled:opacity-50 ${transLang === code ? "border-brand-red/40 bg-brand-red/15 text-brand-red" : "border-white/12 bg-white/[0.03] text-muted-foreground hover:text-white"}`}>
                {label}
              </button>
            ))}
            {translating && <Loader2 className="h-3.5 w-3.5 animate-spin text-brand-red" />}
          </div>
        )}
        {descEdit ? (
          <div className="mt-3 space-y-3">
            <textarea value={descDraft} onChange={(e) => setDescDraft(e.target.value)} rows={5}
              placeholder="Please enter a description of the property and be specific on size, location and feel of the property. The more detailed this is, the better the AI agents will work."
              className="w-full rounded-xl border border-white/10 bg-input/60 p-3 text-sm text-white outline-none focus:ring-2 focus:ring-brand-red placeholder:text-muted-foreground/70" />
            <div className="flex gap-2">
              <Button onClick={saveDesc} disabled={savingDesc} className="bg-brand-red hover:bg-brand-redDark text-white">{savingDesc ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save description"}</Button>
              <Button variant="outline" onClick={() => setDescEdit(false)} className="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]">Cancel</Button>
            </div>
          </div>
        ) : p.summary ? (
          transLang !== "original" && transText ? (
            <p dir={transLang === "ar" ? "rtl" : "ltr"} className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground" data-testid="description-text">{transText}</p>
          ) : (
            <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground" data-testid="description-text">{p.summary}</p>
          )
        ) : (
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground/70">
            Please enter a description of the property and be specific on size, location and feel of the property.
            The more detailed this is, the better the AI agents will work.
          </p>
        )}
      </div>

      {/* AI Deal Analysis */}
      <div className="rounded-2xl border border-white/[0.06] bg-card/40 p-5 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-white"><Sparkles className="h-5 w-5 text-brand-red" /> AI Deal Analysis <CreditBadge /></h3>
          <Button onClick={analyse} disabled={analysing || blocked} variant="outline" className="border-brand-red/30 bg-brand-red/10 text-brand-red hover:bg-brand-red/20 disabled:opacity-50">
            {analysing ? <Loader2 className="h-4 w-4 animate-spin" /> : (blocked ? <><Lock className="h-4 w-4" /> Locked</> : "Analyse")}
          </Button>
        </div>
        <div className="mt-4">
          {blocked && !p.analysis ? <BlockedNotice /> : null}
          {p.analysis ? <Markdown text={p.analysis} /> : (!blocked && <p className="text-sm text-muted-foreground">Click <span className="text-brand-red">Analyse</span> for an AI deal breakdown, rating and negotiation script.</p>)}
        </div>
      </div>

      {/* Airbnb Market Position */}
      <div className="rounded-2xl border border-white/[0.06] bg-card/40 p-5 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-white">
            <Sparkles className="h-5 w-5 text-brand-red" /> Airbnb Market Position
            {ab.confidence && <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-400">{ab.confidence} confidence</span>}
          </h3>
          <Button onClick={estimate} disabled={estimating || blocked} variant="outline" className="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07] disabled:opacity-50">
            {estimating ? <Loader2 className="h-4 w-4 animate-spin" /> : (blocked ? <><Lock className="h-4 w-4" /> Locked</> : <><RefreshCw className="h-4 w-4" /> {ab.nightly_mad ? "Refresh" : "Estimate"}</>)}
          </Button>
        </div>

        {blocked && !ab.nightly_mad ? <div className="mt-4"><BlockedNotice /></div> : null}

        {ab.nightly_mad ? (
          <div className="mt-4 space-y-4">
            <div className="rounded-xl border border-brand-red/25 bg-brand-red/5 p-4">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Suggested Nightly Rate</p>
              <p className="font-display text-4xl font-bold text-brand-red">{ab.nightly_mad.toLocaleString()} <span className="text-xl">MAD</span> <span className="text-sm font-normal text-muted-foreground">/ night</span></p>
              <p className="text-xs text-muted-foreground">Range {ab.nightly_min_mad?.toLocaleString()} – {ab.nightly_max_mad?.toLocaleString()} MAD · ≈ {currency.fmt(ab.nightly_mad)} per night</p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-xl border border-white/8 bg-white/[0.02] p-3"><p className="text-[10px] uppercase text-muted-foreground">Mid Occupancy</p><p className="font-display text-xl font-semibold text-white">{ab.occupancy_pct}%</p><p className="text-[10px] text-muted-foreground">{ab.occupancy_range}</p></div>
              <div className="rounded-xl border border-brand-red/20 bg-brand-red/5 p-3"><p className="text-[10px] uppercase text-muted-foreground">Proj. Revenue / mo</p><p className="font-display text-xl font-semibold text-brand-red">{ab.projected_revenue_mad?.toLocaleString()} MAD</p><p className="text-[10px] text-muted-foreground">{currency.fmt(ab.projected_revenue_mad)}</p></div>
              <div className="rounded-xl border border-white/8 bg-white/[0.02] p-3"><p className="text-[10px] uppercase text-muted-foreground">Competition</p><p className="font-display text-xl font-semibold text-white">{ab.competition}</p><p className="text-[10px] text-muted-foreground">{ab.listings_count} listings</p></div>
              <div className="rounded-xl border border-brand-green/20 bg-brand-green/5 p-3"><p className="text-[10px] uppercase text-muted-foreground">Position</p><p className="font-display text-xl font-semibold text-white">{ab.position}</p></div>
            </div>
            {ab.why && <div><p className="text-[11px] uppercase tracking-wide text-muted-foreground">Why this rate</p><p className="mt-1 text-sm leading-relaxed text-muted-foreground">{ab.why}</p></div>}
            {ab.pricing_strategy && <div className="rounded-xl border border-brand-red/20 bg-brand-red/5 p-4"><p className="text-[11px] uppercase tracking-wide text-brand-red">Pricing Strategy</p><p className="mt-1 text-sm leading-relaxed text-white/85">{ab.pricing_strategy}</p></div>}
          </div>
        ) : (!blocked && <p className="mt-4 text-sm text-muted-foreground">Generate an AI Airbnb pricing & revenue estimate for this property.</p>)}

        <div className="mt-5 border-t border-white/[0.06] pt-4">
          <p className="text-sm font-medium text-white">Saved comparables</p>
          <p className="text-xs text-muted-foreground">{(p.comparables || []).length} tracked · feeds into AI estimate</p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <Input value={compUrl} onChange={(e) => setCompUrl(e.target.value)} placeholder="Paste an Airbnb or Booking.com link…"
              className="h-10 bg-input/50 border-white/10 focus-visible:ring-brand-red" />
            <Button onClick={addComp} variant="outline" className="h-10 border-brand-red/30 bg-brand-red/10 text-brand-red hover:bg-brand-red/20"><Plus className="h-4 w-4" /> Add comp</Button>
          </div>
          {(p.comparables || []).map((c, i) => (
            <a key={c.url || i} href={c.url} target="_blank" rel="noreferrer" className="mt-2 block truncate text-xs text-muted-foreground hover:text-white">{c.url}</a>
          ))}
        </div>
      </div>

      {/* Financial Analysis */}
      <FinancialAnalysis property={p} currency={currency} onSave={(body) => patch(body)} />

      {/* ---- Negotiation / Offers ---- */}
      <div className="rounded-2xl border border-white/[0.06] bg-card/40 p-5 sm:p-6" data-testid="negotiation-section">
        <p className="text-center text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{t("Negotiation")}</p>
        {offers.length === 0 && !offerForm ? (
          <div className="py-6 text-center">
            <h3 className="font-display text-2xl font-semibold text-white">{t("No offers yet")}</h3>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">Start tracking your offers, counters and contract events for this property.</p>
            <Button data-testid="start-negotiation-btn" onClick={() => setOfferForm({ type: OFFER_TYPES[0], valueMad: "", note: "" })}
              variant="ghost" className="mt-5 text-white hover:bg-white/[0.06]"><Plus className="h-4 w-4" /> {t("Start negotiation")}</Button>
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            {offers.map((o) => (
              <div key={o.ts} className="flex items-start justify-between gap-3 rounded-xl border border-white/8 bg-white/[0.02] p-3" data-testid="offer-event">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-brand-red/30 bg-brand-red/10 px-2 py-0.5 text-[11px] font-medium text-brand-red">{o.type}</span>
                    {o.valueMad > 0 && <span className="font-display text-sm font-semibold text-white">{o.valueMad.toLocaleString()} MAD</span>}
                    {o.valueMad > 0 && <span className="text-[11px] text-muted-foreground">{currency.fmt(o.valueMad)}</span>}
                  </div>
                  {o.note && <p className="mt-1 text-sm text-muted-foreground">{o.note}</p>}
                  <p className="mt-1 text-[10px] text-muted-foreground/70">{new Date(o.ts).toLocaleString()}</p>
                </div>
                <button onClick={() => removeOffer(o.ts)} className="shrink-0 text-muted-foreground hover:text-brand-red transition-colors"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            {!offerForm && (
              <Button data-testid="add-event-btn" onClick={() => setOfferForm({ type: OFFER_TYPES[0], valueMad: "", note: "" })}
                variant="ghost" className="text-white hover:bg-white/[0.06]"><Plus className="h-4 w-4" /> Add event</Button>
            )}
          </div>
        )}

        {offerForm && (
          <div className="mt-5 space-y-3" data-testid="offer-form">
            <div className="grid gap-3 sm:grid-cols-3">
              <Select value={offerForm.type} onValueChange={(v) => setOfferForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger className="bg-input/60 border-white/10 text-white" data-testid="offer-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="glass-strong border-white/10">
                  {OFFER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="number" value={offerForm.valueMad} onChange={(e) => setOfferForm((f) => ({ ...f, valueMad: e.target.value }))}
                placeholder="MAD value (optional)" className="bg-input/60 border-white/10 focus-visible:ring-brand-red" data-testid="offer-value-input" />
              <Input value={offerForm.note} onChange={(e) => setOfferForm((f) => ({ ...f, note: e.target.value }))}
                placeholder="Note (optional)" className="bg-input/60 border-white/10 focus-visible:ring-brand-red" data-testid="offer-note-input" />
            </div>
            <div className="flex gap-2">
              <Button data-testid="save-event-btn" onClick={saveOffer} className="bg-brand-red hover:bg-brand-redDark text-white">Save event</Button>
              <Button variant="outline" onClick={() => setOfferForm(null)} className="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]">Cancel</Button>
            </div>
          </div>
        )}
      </div>

      {/* ---- Property notes ---- */}
      <div className="rounded-2xl border border-white/[0.06] bg-card/40 p-5 sm:p-6">
        <NotesPanel entityType="property" entityId={id} title="Property notes" />
      </div>

      {/* ---- Delete confirm + countdown modal ---- */}
      <Dialog open={delOpen} onOpenChange={closeDel}>
        <DialogContent className="max-w-md glass-strong border-brand-red/20" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-white">Delete property?</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This permanently removes <span className="text-white">{p.title}</span>. <span className="text-brand-red">Once deleted, you can't get this back.</span>
            </DialogDescription>
          </DialogHeader>

          {delProgress === null ? (
            <div className="space-y-3 pt-1">
              <label className="text-sm text-muted-foreground">Type <span className="font-semibold text-white">DELETE</span> to confirm</label>
              <Input data-testid="delete-confirm-input" autoFocus value={delText} onChange={(e) => setDelText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && startDelete()} placeholder="DELETE"
                className="bg-input/60 border-white/10 focus-visible:ring-brand-red" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => closeDel(false)} className="flex-1 h-11 border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]">Cancel</Button>
                <Button data-testid="delete-confirm-btn" onClick={startDelete} disabled={delText.trim().toUpperCase() !== "DELETE"}
                  className="flex-1 h-11 bg-brand-red hover:bg-brand-redDark text-white font-medium disabled:opacity-40">
                  <Trash2 className="h-4 w-4" /> Delete
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 pt-1" data-testid="delete-countdown">
              <p className="text-sm text-white">Deleting in {Math.max(0, Math.ceil((100 - delProgress) / 100 * 7))}s…</p>
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-white/10">
                <div className="h-full rounded-full bg-brand-red transition-[width] duration-75 ease-linear" style={{ width: `${delProgress}%` }} />
              </div>
              <Button data-testid="delete-undo-btn" onClick={undoDelete}
                className="h-11 w-full bg-white text-black hover:bg-white/90 font-medium">
                Undo
              </Button>
              <p className="text-center text-[11px] text-muted-foreground">Once deleted, you can't get this back.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ---- Edit specs modal ---- */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md glass-strong border-white/10">
          <DialogHeader><DialogTitle className="font-display text-xl text-white">{t("Edit property")}</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-1">
            <div><label className="text-xs text-muted-foreground">Title</label><Input data-testid="edit-title-input" value={edit.title || ""} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="mt-1 bg-input/60 border-white/10 focus-visible:ring-brand-red" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs text-muted-foreground">Beds</label><Input type="number" value={edit.beds} onChange={(e) => setEdit({ ...edit, beds: e.target.value })} className="mt-1 bg-input/60 border-white/10 focus-visible:ring-brand-red" /></div>
              <div><label className="text-xs text-muted-foreground">Baths</label><Input type="number" value={edit.baths} onChange={(e) => setEdit({ ...edit, baths: e.target.value })} className="mt-1 bg-input/60 border-white/10 focus-visible:ring-brand-red" /></div>
              <div><label className="text-xs text-muted-foreground">m²</label><Input type="number" value={edit.size} onChange={(e) => setEdit({ ...edit, size: e.target.value })} className="mt-1 bg-input/60 border-white/10 focus-visible:ring-brand-red" /></div>
            </div>
            <div><label className="text-xs text-muted-foreground">Price (MAD)</label><Input type="number" value={edit.priceMad} onChange={(e) => onEditPrice(e.target.value)} className="mt-1 bg-input/60 border-white/10 focus-visible:ring-brand-red" /></div>
            <div>
              <label className="text-xs text-muted-foreground">Type</label>
              <div className="mt-1 flex rounded-xl border border-white/10 bg-white/[0.03] p-1">
                {[["rental", "Rent"], ["purchase", "Purchase"]].map(([val, lbl]) => (
                  <button key={val} onClick={() => setEdit({ ...edit, listingType: val, _touchedType: true })}
                    className={`flex-1 rounded-lg py-2 text-sm font-medium transition-colors ${edit.listingType === val ? "bg-brand-red text-white" : "text-muted-foreground hover:text-white"}`}>{lbl}</button>
                ))}
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">Default: under 200,000 MAD = Rent, above = Purchase. You can override.</p>
            </div>
            <Button onClick={saveEdit} className="h-11 w-full bg-brand-red hover:bg-brand-redDark text-white font-medium">Save changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ---- Add city/area modal ---- */}
      <Dialog open={!!addLoc} onOpenChange={(o) => !o && setAddLoc(null)}>
        <DialogContent className="max-w-sm glass-strong border-white/10">
          <DialogHeader><DialogTitle className="font-display text-lg text-white">Add {addLoc?.kind}</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-3 pt-1">
            <div className="flex gap-2">
              <Input autoFocus value={addLoc?.name || ""} onChange={(e) => setAddLoc((a) => ({ ...a, name: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && saveLoc()} placeholder={addLoc?.kind === "city" ? "e.g. Casablanca" : "e.g. Targa"}
                className="bg-input/60 border-white/10 focus-visible:ring-brand-red" />
              <Button onClick={saveLoc} className="bg-brand-red hover:bg-brand-redDark text-white">Add</Button>
            </div>
            {(() => {
              const list = addLoc?.kind === "city" ? cities : areas;
              const def = addLoc?.kind === "city" ? defaults.cities : defaults.areas;
              const custom = list.filter((n) => !def.includes(n));
              if (!custom.length) return <p className="text-[11px] text-muted-foreground">Default {addLoc?.kind === "city" ? "cities" : "areas"} can't be deleted. Your added ones will appear here.</p>;
              return (
                <div className="space-y-1.5">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Your added {addLoc?.kind === "city" ? "cities" : "areas"}</p>
                  {custom.map((n) => (
                    <div key={n} className="flex items-center justify-between rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2">
                      <span className="text-sm text-white">{n}</span>
                      <button onClick={() => deleteLoc(addLoc.kind, n)} className="text-muted-foreground hover:text-brand-red transition-colors"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* ---- Watch how + add key ---- */}
      <Dialog open={howOpen} onOpenChange={setHowOpen}>
        <DialogContent className="max-w-lg glass-strong border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-white">Add your own Claude key</DialogTitle>
            <DialogDescription className="text-muted-foreground">You've used your $3 of free AI. Add a Claude (Anthropic) API key to keep running AI — billed to your own account.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div className="overflow-hidden rounded-xl border border-white/10 bg-black/40">
              {LOOM_HELP_URL ? (
                <div className="aspect-video"><iframe title="How to add your Claude key" src={LOOM_HELP_URL} allowFullScreen className="h-full w-full" /></div>
              ) : (
                <div className="flex aspect-video flex-col items-center justify-center gap-2 text-center">
                  <PlayCircle className="h-10 w-10 text-brand-red" /><p className="text-sm text-white">Walkthrough video coming soon</p>
                  <p className="text-xs text-muted-foreground">Your Loom guide will play here.</p>
                </div>
              )}
            </div>
            <ol className="space-y-1.5 text-xs text-muted-foreground">
              <li>1. Go to <span className="text-white">console.anthropic.com</span> → API Keys</li>
              <li>2. Create a key and copy it (starts with <span className="text-white">sk-ant-…</span>)</li>
              <li>3. Paste it below and save</li>
            </ol>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input value={keyInput} onChange={(e) => setKeyInput(e.target.value)} placeholder="sk-ant-…" type="password" className="h-11 bg-input/60 border-white/10 focus-visible:ring-brand-red" />
              <Button onClick={saveKey} disabled={savingKey} className="h-11 bg-brand-red hover:bg-brand-redDark text-white">{savingKey ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4" /> Save key</>}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
